import { useMemo } from 'react';
import { GetTripsApiResponse, useGetTripsQuery } from '../../store/backendApi';
import { fullImage, imgUrl } from '../../utils/images';
import { getEditTripUrl } from './useMyTripsNavigation';

export interface TransformedTrip {
  title: string;
  href: string;
  imageUrl: string;
  startDate: string;
  endDate: string;
  count: string;
  ownerId: string;
}

/**
 * Platform-agnostic hook for managing My Trips page data.
 * Handles trip fetching, transformation, and derived data like latest trip and hero image.
 */
export function useMyTripsData() {
  const { data: tripsData, isLoading: areTripsLoading } = useGetTripsQuery('admin');

  // Find the latest trip by start date
  const latestTrip = useMemo(() => {
    if (!tripsData || tripsData.length === 0) return null;
    return [...tripsData].sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
    )[0];
  }, [tripsData]);

  // Compute hero image from latest trip
  const heroImage = useMemo(() => {
    return latestTrip?.headerFileName
      ? imgUrl(latestTrip.id, fullImage(latestTrip.headerFileName))
      : '/default-trip-header.jpg';
  }, [latestTrip]);

  // Transform trips for display
  const transformedTrips = useMemo((): TransformedTrip[] | undefined => {
    if (!tripsData) return undefined;
    return tripsData.map((trip) => tripToDisplayFormat(trip));
  }, [tripsData]);

  // Generate href for latest trip (for declarative navigation)
  const latestTripHref = useMemo(() => {
    return latestTrip ? getEditTripUrl(latestTrip.id) : undefined;
  }, [latestTrip]);

  return {
    trips: transformedTrips,
    isLoading: areTripsLoading,
    latestTrip,
    latestTripHref,
    heroImage,
    hasTrips: (tripsData?.length ?? 0) > 0,
  };
}

/**
 * Transform a trip from API format to display format
 */
function tripToDisplayFormat(trip: GetTripsApiResponse[number]): TransformedTrip {
  return {
    title: trip.title,
    href: `/trips/${trip.id}`,
    imageUrl: trip.headerFileName
      ? imgUrl(trip.id, fullImage(trip.headerFileName))
      : '/default-trip-header.jpg',
    startDate: trip.startDate,
    endDate: trip.endDate,
    count: `${trip.postCount} ${trip.postCount === 1 ? 'post' : 'posts'}`,
    ownerId: trip.ownerId,
  };
}
